import { motion } from "framer-motion";
import { BRAND } from "@/styles/brand";

const C = BRAND.colors;

const TIERS = {
  saemling: {
    name: "Saemling",
    emoji: "🌱",
    color: C.green,
    bgColor: C.green + "12",
    minPoints: 0,
    maxPoints: 500,
    benefits: ["2 Willkommens-Stempel", "Geburtstags-Coupon", "Punkte sammeln"],
  },
  gaertner: {
    name: "Gaertner",
    emoji: "🌿",
    color: C.tan,
    bgColor: C.tan + "20",
    minPoints: 500,
    maxPoints: 2000,
    benefits: ["Alles vom Saemling", "Doppelte Punkte am Dienstag", "Gratis Topping", "Exklusive Wochen-Deals"],
  },
  farmer: {
    name: "Farmer",
    emoji: "🌾",
    color: C.orange,
    bgColor: C.orange + "15",
    minPoints: 2000,
    maxPoints: Infinity,
    benefits: ["Alles vom Gaertner", "Gratis Geburtstags-Getränk", "VIP Events", "Überraschungs-Rewards", "Priority Reservierung"],
  },
};

export default function TierBadge({ currentTier = "saemling", points = 350, compact = false }) {
  const tier = TIERS[currentTier];
  const tierKeys = Object.keys(TIERS);
  const currentIdx = tierKeys.indexOf(currentTier);
  const nextTierKey = tierKeys[currentIdx + 1];
  const nextTier = nextTierKey ? TIERS[nextTierKey] : null;

  const progress = nextTier
    ? ((points - tier.minPoints) / (nextTier.minPoints - tier.minPoints)) * 100
    : 100;

  if (compact) {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full"
        style={{ backgroundColor: tier.bgColor }}>
        <span className="text-sm">{tier.emoji}</span>
        <span className="text-[12px] font-bold" style={{ color: tier.color }}>
          {tier.name}
        </span>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className="p-5 relative overflow-hidden"
      style={{
        borderRadius: 24,
        background: `linear-gradient(145deg, ${C.bgCard} 0%, ${tier.bgColor} 100%)`,
        boxShadow: BRAND.shadows.md,
      }}
    >
      {/* Decorative circles */}
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full"
        style={{ backgroundColor: tier.color + "08" }} />

      {/* Badge Circle */}
      <div className="flex items-center gap-4 mb-4">
        <div className="relative">
          {/* Progress Ring */}
          <svg width="64" height="64" viewBox="0 0 64 64">
            <circle cx="32" cy="32" r="28" fill="none" stroke={C.border} strokeWidth="4" />
            <motion.circle
              cx="32" cy="32" r="28" fill="none"
              stroke={tier.color} strokeWidth="4" strokeLinecap="round"
              strokeDasharray={`${2 * Math.PI * 28}`}
              initial={{ strokeDashoffset: 2 * Math.PI * 28 }}
              animate={{ strokeDashoffset: 2 * Math.PI * 28 * (1 - progress / 100) }}
              transition={{ duration: 1, ease: "easeOut" }}
              transform="rotate(-90 32 32)"
            />
          </svg>
          <div className="absolute inset-0 flex items-center justify-center">
            <span className="text-2xl">{tier.emoji}</span>
          </div>
        </div>

        <div className="flex-1">
          <p className="text-[12px] font-medium" style={{ color: C.textMuted }}>
            Dein Level
          </p>
          <h3 className="text-xl font-bold" style={{ color: tier.color, fontFamily: BRAND.typography.heading }}>
            {tier.name}
          </h3>
          {nextTier && (
            <p className="text-[12px] mt-0.5" style={{ color: C.textMuted }}>
              Noch {nextTier.minPoints - points} Punkte bis <strong style={{ color: nextTier.color }}>{nextTier.name}</strong>
            </p>
          )}
          {!nextTier && (
            <p className="text-[12px] mt-0.5" style={{ color: tier.color }}>
              Hoechstes Level erreicht!
            </p>
          )}
        </div>
      </div>

      {/* Tier Steps */}
      <div className="flex items-center gap-1 mb-4">
        {tierKeys.map((key, i) => {
          const t = TIERS[key];
          const isActive = i <= currentIdx;
          return (
            <div key={key} className="flex items-center flex-1">
              <div className="w-full h-1.5 rounded-full"
                style={{ backgroundColor: isActive ? t.color : C.border }} />
            </div>
          );
        })}
      </div>

      {/* Benefits */}
      <div className="space-y-2">
        <p className="text-[11px] font-bold uppercase tracking-wider"
          style={{ color: tier.color }}>
          Deine Vorteile
        </p>
        {tier.benefits.map((b, i) => (
          <div key={i} className="flex items-center gap-2">
            <span className="text-[12px]" style={{ color: tier.color }}>&#10003;</span>
            <span className="text-[13px]" style={{ color: C.text }}>{b}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
}
